import { FormControl, FormHelperText } from "@material-ui/core";
import { makeStyles } from "@saleor/macaw-ui";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import NumberFormat from "react-number-format";

import MyTypography from "../MyTypography";
import { ISelectFieldProps } from "../SingleSelectField";

interface NumberFormatProps extends ISelectFieldProps {
  id?: string;
  testId?: string;
  value?: string;
  isReset?: boolean;
  multiline?: boolean;
  size?: Partial<32 | 40 | 48 | 56>;
  className?: string;
  readOnly?: boolean;
  customClass?: string;
  inputDisabled?: boolean;
  decimalScale?: number;
  allowLeadingZeros?: boolean;
  allowNegative?: boolean;
  rightAlignPlaceholder?: boolean;
  fixedDecimalScale?: boolean;
  onBlur?: (event: any) => void;
  isAllowed?: (value: any) => boolean;
}

const useStyles = makeStyles(
  theme => ({
    input: {
      border: "none",
      outline: `1px solid ${theme.palette.grey[400]}`,
      padding: "0.5rem 1.6rem",
      fontSize: "1.4rem",
      lineHeight: "22px",
      color: theme.palette.grey[900],
      borderRadius: "4px",
      overflow: "hidden",
      "&:hover": {
        "&:not($disabled)": {
          outline: `1px solid ${theme.palette.info.main}`,
          boxShadow: "0px 0px 0px 3px rgb(1 178 237 / 8%)"
        }
      },
      "&:focus": {
        "&:not($disabled)": {
          outline: `1px solid ${theme.palette.info.main}`,
          boxShadow: "0px 0px 0px 3px rgb(1 178 237 / 8%)"
        }
      },
      textAlign: rightAlignPlaceholder =>
        rightAlignPlaceholder ? "right" : "left",
      "&::-webkit-input-placeholder": {
        color: theme.palette.grey[500]
      },
      "&:-ms-input-placeholder": {
        color: theme.palette.grey[500]
      },
      "&::placeholder": {
        color: theme.palette.grey[500],
        textAlign: rightAlignPlaceholder =>
          rightAlignPlaceholder ? "right" : "left"
      }
    },
    error: {
      color: theme.palette.error.main,
      outline: `1.5px solid ${theme.palette.error.light}`,
      "&:hover": {
        "&:not($disabled)": {
          outline: `1.5px solid ${theme.palette.error.light}`
        }
      },
      "&:focus": {
        "&:not($disabled)": {
          outline: `1.5px solid ${theme.palette.error.light}`
        }
      }
    },
    errorWithValue: {
      color: theme.palette.grey[900],
      outline: `1.5px solid ${theme.palette.error.light}`,
      background: theme.palette.scgPrimary.hover
    }
  }),
  { name: "NumberFormat" }
);

const NumberFormatInput: React.FC<NumberFormatProps> = props => {
  const {
    id,
    testId,
    disabled,
    value,
    onChange,
    name,
    hint,
    placeholder,
    error,
    isReset,
    label,
    required,
    customClass,
    inputDisabled,
    decimalScale = 0,
    allowLeadingZeros,
    allowNegative = false,
    rightAlignPlaceholder = false,
    fixedDecimalScale = false,
    onBlur,
    isAllowed
  } = props;
  const classes = useStyles(rightAlignPlaceholder);
  const [textValue, setValue] = useState(value);
  const isChanged = useRef(false);

  const handleChange = event => {
    isChanged.current = true;
    setValue(event.value);
  };

  useEffect(() => {
    if (!isChanged.current) {
      return;
    }
    onChange({
      target: {
        name,
        value: textValue
      }
    });
  }, [textValue]);
  useEffect(() => {
    if (!isChanged.current) {
      return;
    }
    if (isReset) {
      setValue("");
    }
  }, [isReset]);
  useEffect(() => {
    setValue(value);
  }, [value]);
  const isLeadingZeros = values => {
    if (values.floatValue === 0 && allowLeadingZeros) {
      return false;
    }
    return true;
  };
  return (
    <FormControl error={error} disabled={disabled}>
      {label ? (
        <MyTypography variant="body3">
          {label} {required ? <span className={classes.error}>*</span> : ""}
        </MyTypography>
      ) : null}
      <NumberFormat
        id={id}
        data-testid={testId ?? id}
        className={classNames(
          classes.input,
          {
            [classes.error]: error,
            [classes.errorWithValue]: error && textValue,
            "Mui-disabled": inputDisabled
          },
          customClass
        )}
        name={name}
        placeholder={placeholder}
        value={textValue}
        decimalSeparator="."
        displayType="input"
        type="text"
        thousandSeparator={true}
        allowNegative={allowNegative}
        decimalScale={decimalScale}
        onValueChange={handleChange}
        disabled={inputDisabled}
        onBlur={onBlur}
        isAllowed={!!isAllowed ? isAllowed : isLeadingZeros}
        fixedDecimalScale={fixedDecimalScale}
      />
      {hint && <FormHelperText>{hint}</FormHelperText>}
    </FormControl>
  );
};
export default React.memo(NumberFormatInput);
